import React, { useState } from 'react';
import { Calendar, MapPin, Users, Clock, ArrowRight, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

export interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  image: string;
  category: string;
  attendees: number;
  maxAttendees?: number;
}

interface EventCardProps {
  event: Event;
  index?: number;
  onGetTicket?: (event: Event) => void;
}

export function EventCard({ event, index = 0, onGetTicket }: EventCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      full: date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    };
  };

  const dateInfo = formatDate(event.date);
  const attendancePercentage = event.maxAttendees
    ? Math.round((event.attendees / event.maxAttendees) * 100)
    : null;

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1,
        ease: [0.6, 0.05, 0.01, 0.9],
      },
    },
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -12 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="group relative h-full"
    >
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#17265a]/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 -z-10" />

      <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col h-full border border-gray-100/50">
        {/* Event Image with Date Badge */}
        <div className="relative h-64 overflow-hidden">
          <motion.img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover"
            animate={{ scale: isHovered ? 1.08 : 1 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          
          {/* Date Badge - Calendar Style */}
          <motion.div
            className="absolute top-5 left-5 bg-white rounded-lg shadow-2xl overflow-hidden"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              delay: index * 0.1 + 0.3,
              type: 'spring',
              stiffness: 200,
              damping: 15,
            }}
          >
            <div className="bg-[#17265a] text-white text-center py-1.5 px-4">
              <div className="text-xs uppercase tracking-wide">{dateInfo.month}</div>
            </div>
            <div className="px-4 py-2 text-center">
              <div className="text-2xl text-[#17265a]" style={{ fontWeight: '700' }}>
                {dateInfo.day}
              </div>
            </div>
          </motion.div>

          {/* Category Badge */}
          <motion.div
            className="absolute top-5 right-5"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.1 + 0.4 }}
          >
            <div className="px-4 py-2 rounded-lg bg-white/95 backdrop-blur-md shadow-lg border border-white/50">
              <span className="text-sm text-[#17265a]">{event.category}</span>
            </div>
          </motion.div>

          {/* Attendance Progress Bar */}
          {attendancePercentage !== null && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-sm p-3"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 + 0.5 }}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-white/90">Registration</span>
                <span className="text-xs text-white">{attendancePercentage}% Full</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-white to-white/80 rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${attendancePercentage}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + 0.7, duration: 1, ease: 'easeOut' }}
                />
              </div>
            </motion.div>
          )}
        </div>

        {/* Event Content */}
        <div className="p-7 flex flex-col flex-1">
          {/* Title */}
          <h3 className="text-[#17265a] mb-3 group-hover:text-[#0f1a3f] transition-colors line-clamp-2 min-h-[3.6rem]">
            {event.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 mb-6 flex-1 line-clamp-2 leading-relaxed">
            {event.description}
          </p>

          {/* Event Details Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <motion.div
              className="flex items-start gap-2.5 text-gray-700 bg-gray-50 rounded-lg p-3 group/item"
              whileHover={{ scale: 1.03, backgroundColor: '#f8fafc' }}
              transition={{ duration: 0.2 }}
            >
              <div className="w-9 h-9 rounded-md bg-[#17265a]/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#17265a]/20 transition-colors">
                <Clock className="w-4 h-4 text-[#17265a]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">Time</div>
                <div className="text-sm truncate">{event.time}</div>
              </div>
            </motion.div>

            <motion.div
              className="flex items-start gap-2.5 text-gray-700 bg-gray-50 rounded-lg p-3 group/item"
              whileHover={{ scale: 1.03, backgroundColor: '#f8fafc' }}
              transition={{ duration: 0.2 }}
            >
              <div className="w-9 h-9 rounded-md bg-[#17265a]/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#17265a]/20 transition-colors">
                <MapPin className="w-4 h-4 text-[#17265a]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">Location</div>
                <div className="text-sm truncate">{event.location}</div>
              </div>
            </motion.div>

            <motion.div
              className="flex items-start gap-2.5 text-gray-700 bg-gray-50 rounded-lg p-3 col-span-2 group/item"
              whileHover={{ scale: 1.02, backgroundColor: '#f8fafc' }}
              transition={{ duration: 0.2 }}
            >
              <div className="w-9 h-9 rounded-md bg-[#17265a]/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#17265a]/20 transition-colors">
                <Users className="w-4 h-4 text-[#17265a]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">Attendees</div>
                <div className="text-sm">
                  {event.attendees.toLocaleString()} attending
                  {event.maxAttendees && ` of ${event.maxAttendees.toLocaleString()}`}
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onGetTicket?.(event)}
              className="flex-1 bg-[#17265a] text-white py-3.5 px-4 rounded-lg hover:bg-[#0f1a3f] transition-colors duration-200 flex items-center justify-center gap-2 group/btn overflow-hidden relative shadow-md hover:shadow-lg"
            >
              <Eye className="w-5 h-5" />
              <span>View More</span>
              <motion.div
                className="absolute inset-0 bg-white/10"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.5 }}
              />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onGetTicket?.(event)}
              className="w-12 h-12 border-2 border-[#17265a] text-[#17265a] rounded-lg hover:bg-[#17265a] hover:text-white transition-all duration-200 flex items-center justify-center group/icon"
            >
              <motion.div
                animate={{ x: isHovered ? 2 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
